<template>
    <div class="notif-panel">
        <div class="notif-panel-header">
            <span class="notif-panel-title">Notifications</span>
            <div class="notif-panel-actions">
                <button v-if="unreadCount > 0" class="notif-mark-all-btn" @click="markAllRead">
                    Mark all read
                </button>
                <button class="notif-close-btn" @click="$emit('close')" title="Close">
                    <fa :icon="['fas', 'times']" />
                </button>
            </div>
        </div>
        <div class="notif-list">
            <div v-if="loading" class="notif-empty">
                <span class="spinner-border spinner-border-sm text-secondary" role="status"></span>
            </div>
            <transition-group v-else-if="notifications.length > 0" name="notif" tag="div">
                <div
                    v-for="n in notifications"
                    :key="n.id"
                    class="notif-item"
                    :class="{ 'notif-item--unread': !n.read }"
                    @click="markRead(n)"
                >
                    <span class="notif-unread-dot" v-if="!n.read"></span>
                    <div class="notif-body">
                        <p class="notif-message">{{ n.msg }}</p>
                        <span class="notif-time">{{ formatDate(n.timestamp) }} - {{ formatTime(n.timestamp) }}</span>
                    </div>
                    <button class="notif-delete-btn" @click.stop="deleteNotification(n)" title="Delete">
                        <fa :icon="['fas', 'trash']" />
                    </button>
                </div>
            </transition-group>
            <div v-else class="notif-empty">No notifications</div>
        </div>
    </div>
</template>

<style scoped>
.notif-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 21.25rem;
}

.notif-panel-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.7rem 1rem;
    background: #4A74A8;
}

.notif-panel-title {
    font-size: 0.82rem;
    font-weight: 700;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 0.6px;
}

.notif-panel-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.notif-mark-all-btn {
    background: none;
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 5px;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.72rem;
    padding: 2px 0.5rem;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.12s ease;
}

.notif-mark-all-btn:hover {
    background: rgba(255, 255, 255, 0.15);
}

.notif-close-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.625rem;
    height: 1.625rem;
    background: none;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 5px;
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.8rem;
    cursor: pointer;
    transition: background 0.12s ease;
}

.notif-close-btn:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
}

.notif-list {
    flex: 1;
    overflow-y: auto;
    position: relative;
}

.notif-move {
    transition: transform 0.32s ease;
}

.notif-leave-active {
    position: absolute;
    width: 100%;
    animation: notifPoof 0.38s ease forwards;
}

@keyframes notifPoof {
    0%   { transform: scale(1);    opacity: 1;   filter: blur(0px); }
    20%  { transform: scale(0.93); opacity: 1;   filter: blur(0px); }
    55%  { transform: scale(1.07); opacity: 0.7; filter: blur(0px); }
    100% { transform: scale(0);    opacity: 0;   filter: blur(4px); }
}

.notif-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2.5rem 1rem;
    font-size: 0.83rem;
    color: #6c757d;
    gap: 0.5rem;
}

.notif-item {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.85rem 1rem 2rem;
    border-bottom: 1px solid #f0f4f9;
    cursor: pointer;
    position: relative;
    transition: background 0.12s ease;
}

.notif-item:last-child {
    border-bottom: none;
}

.notif-item:hover {
    background: #f8fafc;
}

.notif-item--unread {
    background: #eef3f9;
}

.notif-item--unread:hover {
    background: #e2ecf6;
}

.notif-unread-dot {
    flex-shrink: 0;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: #4A74A8;
    margin-top: 0.3125rem;
}

.notif-body {
    flex: 1;
    min-width: 0;
}

.notif-message {
    font-size: 0.82rem;
    color: #344767;
    margin: 0 0 0.1875rem;
    line-height: 1.4;
    word-break: break-word;
    white-space: pre-line;
}

.notif-item--unread .notif-message {
    font-weight: 600;
    color: #063c6d;
}

.notif-time {
    font-size: 0.72rem;
    color: #6c757d;
}

.notif-delete-btn {
    position: absolute;
    bottom: 0.375rem;
    right: 0.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    background: none;
    border: none;
    border-radius: 4px;
    color: #adb5bd;
    font-size: 0.72rem;
    cursor: pointer;
    transition: background 0.12s ease, color 0.12s ease;
}

.notif-delete-btn:hover {
    background: #f8d7da;
    color: #721c24;
}
</style>

<script>
import axios from 'axios';

export default {
    name: 'TRT_Notifications',
    props: ['user'],
    data() {
        return {
            notifications: [],
            loading: false,
            pollInterval: null
        };
    },
    computed: {
        unreadCount() {
            return this.notifications.filter(n => !n.read).length;
        }
    },
    watch: {
        unreadCount(val) {
            this.$emit('unread-count', val);
        }
    },
    methods: {
        fetchNotifications() {
            if (!sessionStorage.token) return;
            if (this.notifications.length === 0) this.loading = true;
            axios
                .get(process.env.API_LOCATION + '/api/trailReviewTool/notifications', {
                    headers: { 'x-auth-token': sessionStorage.token },
                    params: {
                        user_id: this.user.id
                    }
                })
                .then(res => {
                    this.notifications = res.data;
                    this.loading = false;
                })
                .catch(err => {
                    console.log(err);
                    this.loading = false;
                });
        },
        markRead(notification) {
            if (notification.read) return;
            axios
                .put(
                    process.env.API_LOCATION + '/api/trailReviewTool/notifications/' + notification.id + '/read',
                    {},
                    { headers: { 'x-auth-token': sessionStorage.token } }
                )
                .then(() => {
                    notification.read = true;
                })
                .catch(err => console.log(err));
        },
        deleteNotification(notification) {
            axios
                .delete(
                    process.env.API_LOCATION + '/api/trailReviewTool/notifications/' + notification.id,
                    { headers: { 'x-auth-token': sessionStorage.token } }
                )
                .then(() => {
                    const idx = this.notifications.indexOf(notification);
                    if (idx !== -1) this.notifications.splice(idx, 1);
                })
                .catch(err => console.log(err));
        },
        markAllRead() {
            axios
                .put(
                    process.env.API_LOCATION + '/api/trailReviewTool/notifications/read-all',
                    { receive_user_id: this.user.id },
                    { headers: { 'x-auth-token': sessionStorage.token } }
                )
                .then(() => {
                    this.notifications.forEach(n => { n.read = true; });
                })
                .catch(err => console.log(err));
        },
        formatTime(datetime) {
            if (!datetime) return '';
            const d = new Date(datetime);
            const diff = Math.floor((Date.now() - d) / 1000);
            if (diff < 60) return 'just now';
            if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
            if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
            if (diff < 604800) return Math.floor(diff / 86400) + 'd ago';
            return d.toLocaleDateString(navigator.language, { month: 'short', day: 'numeric', year: 'numeric' });
        },
        formatDate(datetime) {
            const d = new Date(datetime);

            return d.toLocaleDateString(navigator.language, { month:'2-digit', day: '2-digit', year: 'numeric'}) 
                    + ", " + d.toLocaleTimeString(navigator.language, {hour: '2-digit', minute:'2-digit'})
        }
    },
    mounted() {
        this.fetchNotifications();
        this.pollInterval = setInterval(this.fetchNotifications, 30000);
    },
    beforeDestroy() {
        clearInterval(this.pollInterval);
    }
};
</script>
